import { useState, useEffect, useCallback } from 'react'
import { getTrabajoOt, detenerTrabajoOt, getPersonasAsignables } from '../../api/client'
import { useAsignarOt } from './useAsignarOt'
import { formatFechaHora } from '../unidades/tipos'

const fmt = (s) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60)
  return h ? `${h} h ${String(m).padStart(2, '0')} min` : `${m} min`
}

// The OT panel's "Trabajo": who is working on it now and who did, and -- with
// "asignar_personas", while the OT is in execution -- putting someone to work
// on it (whatever else they were on pauses) or stopping them. Assigning goes
// through useAsignarOt, like the board: an OT without its planning asks for
// it, outside working hours asks to authorize overtime (ot_tiempo.py).
export default function TrabajoOt({ ot, puedeAsignar, t, onCambio }) {
  const [trabajo, setTrabajo] = useState(null)
  const [personas, setPersonas] = useState([])
  const [persona, setPersona] = useState('')
  const [aviso, setAviso] = useState(null)        // { texto, error }
  const [ocupado, setOcupado] = useState(false)
  const activo = ot.estado === 'en_ejecucion' && !ot.parte

  const cargar = useCallback(() => {
    getTrabajoOt(ot.id).then(setTrabajo).catch(() => setTrabajo([]))
  }, [ot.id])
  useEffect(() => { cargar() }, [cargar, ot.estado])
  useEffect(() => { if (puedeAsignar && activo) getPersonasAsignables().then(setPersonas).catch(() => {}) }, [puedeAsignar, activo])

  const { asignar, ocupado: asignando, ui } = useAsignarOt({ onHecho: () => { setPersona(''); cargar(); onCambio?.() } })
  const iniciar = () => {
    const p = personas.find(x => String(x.id) === persona)
    if (p) asignar(p, ot)
  }
  const detener = async (id) => {
    setOcupado(true)
    try { await detenerTrabajoOt(id); cargar(); onCambio?.() } catch { setAviso({ texto: t('ot.panel.fallo'), error: true }) }
    setOcupado(false)
  }

  if (trabajo === null) return null
  if (!trabajo.length && !(puedeAsignar && activo)) return null
  const abiertos = trabajo.filter(x => x.abierto)
  const total = trabajo.reduce((s, x) => s + x.segundos, 0)
  const ocupados = new Set(abiertos.map(x => x.persona_id))

  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
        {t('ot.trabajoOt.titulo')}{total > 0 && <span className="ml-1 normal-case text-gray-400">· {fmt(total)}</span>}
      </h3>
      {puedeAsignar && activo && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <select value={persona} onChange={e => { setPersona(e.target.value); setAviso(null) }}
            className="flex-1 min-w-[180px] border border-gray-200 rounded-md px-2 py-1.5 text-sm bg-white">
            <option value="">{t('ot.trabajoOt.elegir')}</option>
            {personas.filter(p => !ocupados.has(p.id)).map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
          </select>
          <button type="button" disabled={!persona || ocupado || asignando} onClick={iniciar}
            className="bg-gray-900 text-white px-3 py-1.5 rounded-md text-xs font-medium hover:bg-gray-800 disabled:opacity-40">
            ▶ {t('ot.trabajoOt.iniciar')}
          </button>
        </div>
      )}
      {!activo && puedeAsignar && !ot.parte && <p className="mb-2 text-xs text-gray-500">{t('ot.trabajoOt.soloEnEjecucion')}</p>}
      {aviso && (
        <div className={`mb-3 rounded-md px-3 py-2 text-xs ${aviso.error ? 'bg-rose-50 text-rose-800' : 'bg-emerald-50 text-emerald-900'}`}>
          {aviso.texto}
        </div>
      )}
      {ui}
      {abiertos.length > 0 && (
        <ul className="mb-2 space-y-1">
          {abiertos.map(x => (
            <li key={x.id} className="flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-1.5 text-sm">
              <span className="text-emerald-700">●</span>
              <span className="flex-1 min-w-0 truncate font-medium text-gray-900">{x.persona_nombre}</span>
              <span className="text-xs text-gray-600">{x.empieza_en > 0 ? t('ot.trabajoOt.vuelveColacion') : fmt(x.segundos)}</span>
              {x.hora_extra && <span className="text-[10px] rounded bg-amber-100 px-1 text-amber-800">{t('ot.trabajoOt.extra')}</span>}
              {puedeAsignar && (
                <button type="button" disabled={ocupado} onClick={() => detener(x.id)}
                  className="text-xs font-medium text-gray-600 border border-gray-200 bg-white rounded px-2 py-0.5 hover:bg-gray-50">■ {t('ot.trabajoOt.detener')}</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {trabajo.some(x => !x.abierto) && (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg text-xs">
          {trabajo.filter(x => !x.abierto).slice(0, 30).map(x => (
            <li key={x.id} className="flex items-center gap-2 px-3 py-1.5">
              <span className="flex-1 min-w-0 truncate text-gray-800">{x.persona_nombre}</span>
              <span className="text-gray-500">{formatFechaHora(x.inicio)}</span>
              <span className="w-20 text-right tabular-nums text-gray-900">{fmt(x.segundos)}</span>
              <span className="w-24 text-right text-gray-400">{t(`ot.trabajoOt.motivo.${x.motivo_fin}`, { defaultValue: x.motivo_fin || '' })}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
